import React, { useEffect, useState } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Box, Button } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { CustomPagination } from 'common/components';
import ZapAccessRestrictedBanner from 'common/modules/zap/components/ZapAccessRestrictedBanner';
import { parseDate, formatDate, formatPeriodLabel } from 'common/utils/dates';
import { numberFormat } from 'common/utils/helpers';
import {
    getDateFormatByLocale,
    getLocaleEquivalenceForDateFns,
} from 'common/utils/multiregion';
import { getCountryCurrencyLocale } from 'common/utils/session';

import * as supplierIntegrationsSelectors from '../../../../modules/supplierIntegrations/selectors';
import { getPeriods } from '../actions';

const RpuPeriods = ({ onCancel, onSubmit, rpuId, submitText }) => {
    const [periods, setPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [isLoadingPeriods, setIsLoadingPeriods] = useState(true);

    const { t } = useTranslation();

    const dateFormat = getDateFormatByLocale();
    const localeForDateFns = getLocaleEquivalenceForDateFns();

    useEffect(() => {
        if (!rpuId) return;

        setIsLoadingPeriods(true);
        getPeriods({ limit: 24, rpuId })
            .then((response) => {
                const data = response?.data
                    ?.map(({ final_date, initial_date, ...rest } = {}) => ({
                        ...rest,
                        final_date: parseDate(
                            final_date,
                            'yyyy-MM-dd',
                            localeForDateFns,
                        ),
                        initial_date: parseDate(
                            initial_date,
                            'yyyy-MM-dd',
                            localeForDateFns,
                        ),
                    }))
                    .sort((a, b) => b?.final_date - a?.final_date)
                    .map((period) => ({
                        ...period,
                        final_date: formatDate(
                            period?.final_date,
                            dateFormat,
                            localeForDateFns,
                        ),
                        label: formatPeriodLabel({
                            finalDate: period?.final_date,
                            isBimonthly: period?.is_bimonthly,
                            locale: localeForDateFns,
                        }),
                        initial_date: formatDate(
                            period?.initial_date,
                            dateFormat,
                            localeForDateFns,
                        ),
                    }));

                setPeriods(data);
            })
            .catch(() => setPeriods([]))
            .finally(() => setIsLoadingPeriods(false));
    }, [rpuId]);

    if (!rpuId) return null;

    const countryCurrencyLocale = getCountryCurrencyLocale();
    const numberFormatOptions = {
        decimals: 0,
        locale: countryCurrencyLocale,
        style: 'decimal',
    };
    const onRowClick = (params) => setSelectedPeriod(params.row);

    const columns = [
        {
            field: 'label',
            headerName: t('Period'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'initial_date',
            headerName: t('Initial date'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'final_date',
            headerName: t('Final date'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'consumption',
            headerName: t('Consumption') + ' (kWh)',
            flex: 1,
            minWidth: 150,
            valueGetter: (_, row) => {
                const totalConsumption = row?.values?.energy?.reduce(
                    (acc, val) => acc + (Number(val?.consumption) || 0),
                    0,
                );
                return numberFormat(totalConsumption, numberFormatOptions);
            },
        },
    ];

    return (
        <>
            <DataGrid
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                getRowId={(row) => row?.period_id}
                hideFooterSelectedRowCount
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                loading={isLoadingPeriods}
                onRowClick={onRowClick}
                pagination
                paginationMode="client"
                selectionModel={
                    selectedPeriod ? [selectedPeriod?.period_id] : []
                }
                rows={periods}
                slotProps={{ pagination: { pageSizeOptions: [5, 10, 15] } }}
                slots={{ pagination: CustomPagination }}
                sx={{
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                }}
            />

            <Box
                display="flex"
                flexDirection={{ md: 'row', xs: 'column' }}
                gap={1}
                justifyContent={{ md: 'right', xs: 'center' }}
                mt={2}
                width="100%"
            >
                {onCancel && (
                    <Button
                        color="secondary"
                        onClick={onCancel}
                        sx={{
                            order: { md: 1, xs: 2 },
                            width: { md: 'auto', xs: '100%' },
                        }}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>
                )}

                <Button
                    disabled={isLoadingPeriods || !selectedPeriod}
                    onClick={() => onSubmit(selectedPeriod)}
                    sx={{
                        order: { md: 2, xs: 1 },
                        width: { md: 'auto', xs: '100%' },
                    }}
                >
                    {submitText || t('Submit')}
                </Button>
            </Box>
        </>
    );
};

RpuPeriods.propTypes = {
    onCancel: PropTypes.func,
    onSubmit: PropTypes.func,
    rpuId: PropTypes.string,
    submitText: PropTypes.string,
};

const Restricted = ({ onCancel, zapIntegration, ...props }) => {
    const { t } = useTranslation();

    if (!zapIntegration?.id)
        return (
            <>
                <ZapAccessRestrictedBanner />

                {onCancel && (
                    <Box mt={2} textAlign="right" width="100%">
                        <Button
                            color="secondary"
                            onClick={onCancel}
                            variant="outlined"
                        >
                            {t('Cancel')}
                        </Button>
                    </Box>
                )}
            </>
        );
    return <RpuPeriods onCancel={onCancel} {...props} />;
};

const mapStateToProps = createStructuredSelector({
    zapIntegration: supplierIntegrationsSelectors.getZapIntegration,
});

Restricted.propTypes = {
    onCancel: PropTypes.func,
    zapIntegration: PropTypes.object,
};

export default connect(mapStateToProps)(Restricted);
